import { DateTime } from "luxon";

export class ObjectUtility {

    /**
     * Converts the properties of an object to strings.
     * 
     * This function takes an object and returns a new object where each 
     * property value has been converted to a string. If a property value 
     * is `null` or `undefined`, it will be omitted from the result. 
     * Additionally, if a property value is an instance of `DateTime`, 
     * it will be converted to an ISO date string.
     * 
     * @param obj - The object whose properties will be converted to strings.
     * @returns A new object with the same keys as the input object, 
     *          but with all values converted to strings.
     */
    static convertPropsToString<T extends {}>(obj: T): { [K in keyof T]: string } {
        const result = {} as { [K in keyof T]: string };
    
        for (const key in obj) {
            if(!Object.prototype.hasOwnProperty.call(obj, key)) continue;

            const value = obj[key];

            if(_.isNil(value)) continue;

            if (value instanceof DateTime) {
                result[key] = value.toISODate();
            } else {
                result[key] = String(value);
            }
        }
    
        return result;
    }

    /**
     * Case-insensitive retrieval of object properties
     * @param object object to parse
     * @param key property to return
     * @returns property if found
     */
    static getPropertyCaseInsensitive(object: any, key: string) {
        const asLowercase = key.toLowerCase();
        const property = Object.keys(object)
            .find(k => k.toLowerCase() === asLowercase);

        if (property) {
            return object[property];
        }
    }

    /**
     * Deep compare two arrays of the same type to determine if they have the same contents.
     */
    static sequenceEquals<T>(arr1: T[], arr2: T[]) {
        return _(arr1).differenceWith(arr2, _.isEqual).isEmpty();
    }
}
